import { useRef, type PointerEvent, type ReactNode } from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface SheetProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: ReactNode
  title: ReactNode
  description?: ReactNode
  /** Hide the title visually (it still names the sheet for screen readers). */
  hideTitle?: boolean
  children: ReactNode
  className?: string
}

/** A drag past this many pixels, or a quick flick, dismisses the sheet. */
const DISMISS_DISTANCE = 80
const DISMISS_VELOCITY = 0.5

/**
 * A bottom sheet for phones: rises from the bottom edge with a grab handle, closes on the scrim,
 * Escape, the close button, or a downward swipe on its header. Clears the home indicator.
 */
export function Sheet({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  hideTitle,
  children,
  className,
}: SheetProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ y: number; t: number; dy: number } | null>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  const setDrag = (dy: number) => contentRef.current?.style.setProperty('--sheet-drag', `${dy}px`)

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return
    drag.current = { y: e.clientY, t: e.timeStamp, dy: 0 }
    e.currentTarget.setPointerCapture(e.pointerId)
    contentRef.current?.style.setProperty('transition', 'none')
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    // Downward follows the finger; upward resists, so the sheet feels anchored.
    const raw = e.clientY - drag.current.y
    const dy = raw > 0 ? raw : raw / 6
    drag.current.dy = dy
    setDrag(dy)
  }
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    drag.current = null
    if (!d) return
    const velocity = d.dy / Math.max(e.timeStamp - d.t, 1)
    const el = contentRef.current
    el?.style.setProperty('transition', 'transform 240ms var(--ease-out)')
    if (d.dy > DISMISS_DISTANCE || velocity > DISMISS_VELOCITY) {
      closeRef.current?.click()
    } else {
      setDrag(0)
    }
  }

  return (
    <RadixDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (next) setDrag(0)
        onOpenChange?.(next)
      }}
    >
      {trigger && <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>}
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="gd-scrim fixed inset-0 z-50 bg-[var(--scrim)] backdrop-blur-[2px]" />
        <RadixDialog.Content
          ref={contentRef}
          {...(!description && { 'aria-describedby': undefined })}
          className={cn(
            'gd-sheet fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[88dvh] w-full max-w-lg flex-col',
            'rounded-t-xl border-t border-line bg-surface-pop shadow-pop outline-none',
            className,
          )}
        >
          <div
            className="shrink-0 cursor-grab touch-none select-none px-4 pb-2 pt-2 active:cursor-grabbing"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div aria-hidden="true" className="mx-auto mb-2 h-1 w-9 rounded-full bg-line-strong" />
            <div className="flex items-start justify-between gap-4">
              <div className={cn('min-w-0', hideTitle && 'sr-only')}>
                <RadixDialog.Title className="font-display text-lead font-semibold text-ink">
                  {title}
                </RadixDialog.Title>
                {description && (
                  <RadixDialog.Description className="mt-0.5 text-small text-ink-2">
                    {description}
                  </RadixDialog.Description>
                )}
              </div>
              <RadixDialog.Close asChild>
                <button
                  ref={closeRef}
                  type="button"
                  aria-label="Close"
                  className={cn(
                    '-mr-1.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 hover:text-ink',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                    hideTitle && 'ml-auto',
                  )}
                >
                  <X className="size-4" aria-hidden="true" />
                </button>
              </RadixDialog.Close>
            </div>
          </div>
          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
          >
            {children}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}
